import { FormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";

import { AuthService } from "src/app/core/core-services/services/auth.service";
import { DialogService, DIALOG_WIDTHS } from "src/app/core/core-services/services/dialog.service";
import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { PersonaAiApiService } from "src/app/core/core-services/services/persona-ai-api.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";

import { Location } from "@angular/common";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { ClipboardService } from "ngx-clipboard";
import { Store } from "@ngrx/store";
import { Subject, Subscription, interval, timer } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { intervalToDuration } from "date-fns";
import { WarningDialogComponent } from "src/app/core/core-services/warning-dialog/warning-dialog.component";
import { SharedDataService } from "src/app/core/core-services/services/shared-data.service";
import { LoadingService } from "src/app/core/core-services/services/loader.service";
import { GlobalHookService } from "src/app/global-hook.service";
import { modalStepIndex } from "src/app/core/core-services/services/onboarding-progress.service";
import { CanComponentDeactivate } from "./can-deactivate-form.guard";
import { VngDialogService, VngTourService } from "@vengreso/angular-components";
import { LEAVE_PERSONA_CONFIRM } from "src/app/vengreso/user-persona-add/can-deactivate-form.guard";
import { selectCanUpgrade } from "src/app/core/store/plan/plan.selectors";
import { PersonaPromptsQuota, PersonaQuotaMeta } from "src/app/core/models/persona.model";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { SignInErrorComponent } from "src/app/session/signin/sign-in-error/sign-in-error.component";

/** Defaults applied whenever the backend hasn't shipped flymsg-be#875 yet
 * (fields simply come back `undefined`) — regeneration reads as allowed for
 * everyone and the free-generation count reads as 1, matching today's
 * behaviour and the Engineering decision recorded on issue #569.
 */
const DEFAULT_QUOTA_META: PersonaQuotaMeta = {
  showUpgradeButton: true,
  isEnterprise: false,
  regenerationAllowed: true,
  freeGenerations: 1,
};

@Component({
    selector: "app-user-persona-add",
    templateUrl: "./user-persona-add.component.html",
    styleUrls: ["./user-persona-add.component.scss"],
    standalone: false
})
export class UserPersonaAddComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @HostListener("window:beforeunload", ["$event"])
  beforeUnloadHandler(event: BeforeUnloadEvent) {
    if (!this.canDeactivate()) {
      event.preventDefault();
      event.returnValue = "";
      return false;
    }
    return true;
  }

  @ViewChild("wrapperContainer") wrapperContainer!: ElementRef<HTMLElement>;
  @ViewChild("footerElement") footerElement!: ElementRef<HTMLElement>;
  searchPersona: string;
  profileData: any;
  form: FormGroup;
  updating: boolean = false;
  quota: PersonaPromptsQuota = {
    used: 0,
    total: 0,
    remaining: 0,
  };
  quotaMeta: PersonaQuotaMeta = { ...DEFAULT_QUOTA_META };
  /** Epoch ms when the daily quota refills; null when unknown/not exhausted. */
  refillAt: number | null = null;
  /** Human "3h 12m" / "45m" / "<1m" rendering of `refillAt`, ticked every second. */
  refillCountdown = "";
  /** True when the saved persona's `ai_emulation` predates the last edit (flymsg-be#875). */
  staleEmulation = false;
  /** Fallback for `quotaMeta.showUpgradeButton` while the backend field is absent. */
  private canUpgrade = true;
  /** Started lazily by setRefillAt() only while there's a countdown to render — see its doc comment. */
  private refillTicker?: Subscription;
  private readonly destroy$ = new Subject<void>();

  userPersonaId?: string;

  toneOptions: { label: string; value: string }[] = [
    { label: "Assertive", value: "67be34e534fdd53681092752" },
    { label: "Casual", value: "67be34e534fdd53681092753" },
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly dialog: DialogService,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
    private readonly dataService: DataService,
    private readonly personaAiApiService: PersonaAiApiService,
    private readonly router: Router,
    private readonly location: Location,
    private readonly notification: NotificationService,
    private readonly sharedData: SharedDataService,
    private readonly loadingService: LoadingService,
    private readonly global: GlobalHookService,
    private readonly tourService: VngTourService,
    private readonly clipboardService: ClipboardService,
    private readonly store: Store,
    private readonly extensionService: ExtensionService,
    private readonly vngDialogService: VngDialogService,
    config: NgbPopoverConfig
  ) {
    config.container = "body";
    config.placement = ["right"];
    config.autoClose = true;
    config.animation = true;

    this.form = this.fb.group({
      name: [null, [Validators.required]],
      professional_interests: [[], [Validators.required, Validators.minLength(1)]],
      first_name: [null],
      last_name: [null],
      industry: [null, [Validators.required]],
      prompt_tone_id: [null, [Validators.required]],
      type: ["person", [Validators.required, Validators.pattern("^(person|company)$")]],
      is_default: [false],
      linkedin_url: [null],
      achievements: [null],
      preferred_keywords_and_phrases: [[]],
      communication_style: [[]],
      company_name: [null],
      education_background: [[]],
      field_of_expertise: [[]],
      goals: [null],
      hobbies: [[]],
      personal_interests: [[]],
      skills: [[]],
      taboos: [[]],
      title: [null],
      content: [null, [Validators.maxLength(1000)]],
      networking: [null, [Validators.maxLength(1000)]],
      personal_info: [null, [Validators.maxLength(1000)]],
      ai_emulation: [{ value: null, disabled: true }, [Validators.maxLength(1000)]],
    });

    this.form.get("type")?.valueChanges.subscribe((value) => this.adjustValidations(value));
  }

  canDeactivate(): boolean {
    return this.form.pristine;
  }

  confirmLeave(): void {
    this.form.markAsPristine();
  }

  private adjustValidations(type: string): void {
    const firstNameControl = this.form.get("first_name");
    const lastNameControl = this.form.get("last_name");
    const companyNameControl = this.form.get("company_name");

    if (type === "person") {
      firstNameControl?.setValidators([Validators.required]);
      lastNameControl?.setValidators([Validators.required]);
      companyNameControl?.clearValidators();
    } else if (type === "company") {
      firstNameControl?.clearValidators();
      lastNameControl?.clearValidators();
      companyNameControl?.setValidators([Validators.required]);
    }

    firstNameControl?.updateValueAndValidity();
    lastNameControl?.updateValueAndValidity();
    companyNameControl?.updateValueAndValidity();
  }

  loadData(personaId: string, duplicate: boolean): void {
    this.loadingService.start();
    this.dataService
      .get({
        url: `v2/user-persona/details/${personaId}`,
        isLoader: true,
      })
      .subscribe(
        (response: any) => {
          if (response?.result) {
            if (response.result.disabled) {
              this.router.navigate(["/persona"]);
              return;
            }

            this.form.patchValue({
              ...response.result,
              name: duplicate ? `${response.result.name} (Copy)` : response.result.name,
            });
            // The nine chip fields are now vng-chip-input controls, so the
            // patchValue above is the single source of truth for them; the
            // parallel class arrays that used to mirror the API payload are gone.
            if (!duplicate) {
              this.form.markAsUntouched();
            }
            // Undefined against today's backend (flymsg-be#875 not merged
            // yet) — the banner below simply never shows, which is today's
            // behaviour. Carried through on duplicate too: the copy inherits
            // whatever staleness the source persona had.
            this.staleEmulation = !!response.result.ai_emulation_stale;
          }
        },
        () => {
          this.loadingService.stop();
        },
        () => {
          this.loadingService.stop();
        }
      );
  }

  /**
   * H6: `VngTourService.renderStep()` has no `scrollIntoView` anywhere — the
   * pre-migration ngx-ui-tour scrolled to each anchor automatically and that
   * behaviour was never ported. Every content step in this component also sets
   * `disablePageScrolling: true`, which parks `document.body.style.overflow`
   * at `hidden`, so a step whose anchor sits below the fold (step 15's
   * "Generate Persona" button lives in the form footer) rendered its popover
   * against an off-screen rect that the user could not scroll to either.
   *
   * The scroll lock can stay: `scrollIntoView` still moves a container whose
   * overflow is `hidden` (unlike `clip`), and the service's own capture-phase
   * `scroll` listener repositions the popover while the smooth scroll runs.
   * Only the real content steps are scrolled — the `-start`/`-end` signal
   * steps are handled (and returned from) by their callers.
   *
   * Resolved by attribute rather than through the tour's anchor registry
   * because `VngTourAnchorRegistryService` is explicitly not part of the
   * library's public API; every anchor id here is a static `vngTourAnchor`
   * attribute, so it is present in the DOM.
   */
  private scrollTourAnchorIntoView(anchorId: string): void {
    if (!anchorId) return;
    const anchor = document.querySelector<HTMLElement>(`[vngTourAnchor="${anchorId}"]`);
    anchor?.scrollIntoView({ behavior: "smooth", block: "center" });
  }

  updatePrompts() {
    this.personaAiApiService.getPromptQuota().subscribe(
      (response) => {
        if (response?.result?.quota) {
          this.applyQuotaPayload(response.result);
        }
      },
      (error) => {
        this.handleNodeDeactivation(error);
      }
    );
  }

  /**
   * `getPromptQuota()`, `getTones()` and `getPersonaQuota()` (issue #582/
   * AI-27) now call flymsg-node directly via `PersonaAiApiService`'s plain
   * `HttpClient`, bypassing `DataService` entirely — so none of them run
   * through `DataService.handleError()`, whose `403 {code:'DEACTIVATED'}`
   * branch (data.service.ts:295) is what normally opens the "your account
   * has been deactivated" dialog and logs the session out. Node's own auth
   * guard rejects a deactivated user's bearer with that exact same shape
   * (`flymsg-node src/middleware/auth.ts:134-144`), so without this a user
   * deactivated mid-session on `/persona/new` — where every remaining call
   * on the page is a node call — would just see a silently empty quota
   * block instead of that dialog (fix-round F2).
   *
   * Deliberately NOT a full port of `handleError()`: only the one branch
   * that actually regressed. Returns whether the error matched, so a caller
   * can decide what (if anything) else to do with an error that wasn't a
   * deactivation.
   */
  private handleNodeDeactivation(error: any): boolean {
    if (error?.status !== 403 || error?.error?.code !== "DEACTIVATED") {
      return false;
    }

    setTimeout(() => {
      const dialogHandle = this.dialog.openDialogComponent(
        SignInErrorComponent,
        { admin_email: error?.error?.admin_email },
        "500px"
      );

      dialogHandle.afterClosed().subscribe(() => {
        this.vngDialogService.closeAll();
        this.extensionService.sendLogoutMessage();
        this.sharedData.clear();
        setTimeout(() => {
          this.router.navigate(["/session/signin"]);
        }, 1000);
      });
    }, 2000);

    return true;
  }

  /**
   * Shared by `updatePrompts()` (`GET v2/meta-data/quota` → `result`) and the
   * generate-persona response (`response.quota`, "same shape as above" per
   * flymsg-be#875) — both hand this the `{ quota, show_upgrade_button,
   * is_enterprise, persona_regeneration }` envelope.
   */
  private applyQuotaPayload(payload: {
    quota: PersonaPromptsQuota;
    show_upgrade_button?: boolean;
    is_enterprise?: boolean;
    persona_regeneration?: { allowed: boolean; free_generations: number };
  }): void {
    this.quota = payload.quota;
    this.quotaMeta = {
      showUpgradeButton: payload.show_upgrade_button ?? this.canUpgrade,
      isEnterprise: payload.is_enterprise ?? DEFAULT_QUOTA_META.isEnterprise,
      regenerationAllowed: payload.persona_regeneration?.allowed ?? DEFAULT_QUOTA_META.regenerationAllowed,
      freeGenerations: payload.persona_regeneration?.free_generations ?? DEFAULT_QUOTA_META.freeGenerations,
    };
    this.setRefillAt(payload.quota.seconds_remaining_until_next_prompt_refill);
  }

  /**
   * (Re)anchors the countdown to now + the backend's seconds-remaining, then
   * renders it immediately. The 1s ticker is started lazily here (and
   * stopped once there's nothing to count down to) rather than
   * unconditionally in ngOnInit — most sessions never exhaust the quota, so
   * this keeps an idle tab from ticking every second for nothing, and
   * (RxJS `interval` is backed by a single reused `setInterval`) keeps
   * `fakeAsync` tests that never touch the quota gate free of a periodic
   * timer they'd otherwise have to know to discard.
   */
  private setRefillAt(secondsRemaining: number | undefined): void {
    if (typeof secondsRemaining === "number" && secondsRemaining > 0) {
      this.refillAt = Date.now() + secondsRemaining * 1000;
      if (!this.refillTicker) {
        this.refillTicker = interval(1000)
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.tickRefillCountdown());
      }
    } else {
      this.refillAt = null;
      this.refillTicker?.unsubscribe();
      this.refillTicker = undefined;
    }
    this.tickRefillCountdown();
  }

  /** Ticked every second while `refillTicker` is running; also called once whenever refillAt changes. */
  private tickRefillCountdown(): void {
    if (!this.refillAt) {
      this.refillCountdown = "";
      return;
    }

    const msRemaining = this.refillAt - Date.now();
    if (msRemaining <= 0) {
      // The quota really did reset server-side at start of day — refetch
      // rather than trust a locally-decremented `remaining`.
      this.refillAt = null;
      this.refillCountdown = "";
      this.refillTicker?.unsubscribe();
      this.refillTicker = undefined;
      this.updatePrompts();
      return;
    }

    const duration = intervalToDuration({ start: 0, end: msRemaining });
    const hours = (duration.days ?? 0) * 24 + (duration.hours ?? 0);
    if (hours > 0) {
      this.refillCountdown = `${hours}h ${duration.minutes ?? 0}m`;
    } else if ((duration.minutes ?? 0) > 0) {
      this.refillCountdown = `${duration.minutes}m`;
    } else {
      this.refillCountdown = "<1m";
    }
  }

  /**
   * True once regeneration is both in-plan and there's quota left today.
   * `total === -1` is unlimited (Sales Pro/Teams); `total === 0` is this
   * component's own initial placeholder before the first updatePrompts()
   * response lands — treated as "unknown, don't block" so a slow first
   * fetch can't briefly disable Regenerate / show the wrong Save hint.
   */
  get canRegenerate(): boolean {
    if (!this.quotaMeta.regenerationAllowed) return false;
    if (this.quota.total === -1 || this.quota.total === 0) return true;
    return this.quota.remaining !== 0;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    // Fallback for quotaMeta.showUpgradeButton while the backend field is
    // absent (flymsg-be#875 not merged yet) — read fresh by applyQuotaPayload()
    // on every fetch, never written back into quotaMeta directly here.
    this.store
      .select(selectCanUpgrade)
      .pipe(takeUntil(this.destroy$))
      .subscribe((canUpgrade) => (this.canUpgrade = canUpgrade));

    if (this.global.onboardingActive) {
      setTimeout(() => {
        // C3-15: `prevStep` (a field only the old Material-menu tour library
        // supported, letting "Back" jump to an arbitrary named step) is
        // dropped — vng-tour's prev() is
        // strictly sequential (currentIndex - 1). Each `prevStep` target below
        // already matches its step's natural sequential predecessor
        // (step15's prevStep "step14" = index1, one before it at index2;
        // step16's prevStep "step15" = index2, one before it at index4 — the
        // intervening "step15-end" signal-step at index3 is never reached
        // going backward because the tour explicitly end()s there going
        // forward, so no reordering is needed here, unlike menu.component.ts's
        // step11FlyPlates case).
        this.tourService.initialize([
          {
            stepId: "step14-start",
            anchorId: "step14-start",
            content: " ",
            title: " ",
          },
          {
            stepId: "step14",
            anchorId: "step14",
            content: "These fields are necessary to create your unique AI persona. Please complete them to continue.",
            title: "Essential Persona Details",
            enableBackdrop: true,
            disablePageScrolling: true,
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
            nextBtnTitle: "Next",
            prevBtnTitle: "Skip",
            popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end",
          },
          {
            stepId: "step15",
            anchorId: "step15",
            content:
              "Click to generate a detailed prompt for FlyMSG AI, based on the information provided. This prompt will guide the AI in emulating the persona you've defined.",
            title: "Create AI Persona Prompt",
            enableBackdrop: true,
            disablePageScrolling: true,
            nextOnAnchorClick: true,
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
            prevBtnTitle: "Back",
            // "Next" is dead on this step: there is nothing to save until the
            // prompt has been generated, and the copy is asking for exactly
            // that. The step advances via nextOnAnchorClick when "Generate
            // Persona" is pressed. "Back" stays live.
            popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end fms-tour-disable-next",
          },
          {
            stepId: "step15-end",
            anchorId: "step15-end",
            content: " ",
            title: " ",
          },
          {
            anchorId: "step16",
            stepId: "step16",
            content:
              "Click to save the generated AI persona prompt. This persona will then be available for use in FlyMSG AI's writing tools.",
            title: "Save Persona",
            enableBackdrop: true,
            disablePageScrolling: true,
            prevBtnTitle: "Back",
            nextBtnTitle: "Got it!",
            placement: {
              xPosition: "before",
              yPosition: "above",
            },
            endBtnTitle: "Got it!",
            popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end",
          },
          {
            anchorId: "step16-end",
            content: " ",
            title: " ",
          },
        ]);

        // Registered exactly once, here in ngOnInit(), rather than again on
        // every generateAIEmulation() completion: this handler is stateless
        // (it only switches on step.anchorId and calls fixed methods), so a
        // second per-generation registration would just be a duplicate
        // listener on the same never-unsubscribed stepShow$ stream — which
        // is exactly what used to cause savePersona() to fire twice from
        // step16-end after a second generation (issue #466). The step16-*
        // cases below cover the tour steps generateAIEmulation() introduces
        // when it re-initializes the tour after a successful generation.
        this.tourService.stepShow$.pipe(takeUntil(this.destroy$)).subscribe((step) => {
          if (step.anchorId === "step14-start") {
            this.returnToOnboardingModal();

            setTimeout(() => {
              this.tourService.end();
            }, 1);

            return;
          }
          if (step.anchorId === "step15-end") {
            this.tourService.end();
            return;
          }
          if (step.anchorId === "step16-start") {
            this.returnToOnboardingModal();

            setTimeout(() => {
              this.tourService.end();
            }, 1);

            return;
          }
          if (step.anchorId === "step16-end") {
            this.savePersona();
            return;
          }

          this.scrollTourAnchorIntoView(step.anchorId);
        });

        setTimeout(() => {
          this.tourService.startAt("step14");
        }, 1000);
      }, 50);
    }

    this.updatePrompts();

    this.personaAiApiService.getTones().subscribe(
      (response) => {
        // Node returns `{result:{data:[]}}` for every FlyAI lookup — the
        // `Array.isArray` guard keeps a non-array/missing `data` from
        // blanking the tone dropdown via a throwing `.map` (issue #582,
        // mirroring the extension's own defensive lookup handling): the
        // hardcoded default tones are left in place unless the response
        // actually carries a real (non-empty) array to replace them with.
        const tones = response?.result?.data;
        if (Array.isArray(tones) && tones.length > 0) {
          this.toneOptions = tones.map((item) => ({
            label: item.name,
            value: item.id,
          }));
        }
      },
      (error) => {
        this.handleNodeDeactivation(error);
      }
    );

    // if route name (not param) contains duplicate
    const duplicate = this.route.snapshot.url.some((url) => url.path === "duplicate");
    let userPersonaId = this.route.snapshot.paramMap.get("userPersonaId");

    if (userPersonaId && !duplicate) {
      this.userPersonaId = userPersonaId;
      this.loadData(userPersonaId, false);
    } else {
      try {
        this.personaAiApiService.getPersonaQuota().subscribe(
          (response: any) => {
            if (response?.result) {
              if (response?.result.remaining < 1) {
                switch (response?.result.quota) {
                  case 1:
                    this.dialog.openDialogComponent(
                      WarningDialogComponent,
                      {
                        type: "persona",
                        showTheStarter: false,
                        error: "maxNumberOfPersonasFree",
                      },
                      "800px"
                    );
                    break;
                  case 3:
                    this.dialog.openDialogComponent(
                      WarningDialogComponent,
                      {
                        type: "persona",
                        showTheStarter: false,
                        pricing: "salesPro",
                        selectedPlan: "salesPro",
                        error: "maxNumberOfPersonasGrowth",
                      },
                      "800px"
                    );
                    break;
                  case 7:
                    this.notification.toast(
                      "You've hit the maximum number of 7 personas for your current plan. Update or delete any existent persona.",
                      "OK",
                      7000
                    );
                    break;
                  default:
                    // Used-up plan without dedicated copy: same generic
                    // upsell as the persona list's gate (issue #591).
                    this.dialog.openDialogComponent(
                      WarningDialogComponent,
                      {
                        type: "persona",
                        showTheStarter: false,
                        pricing: "salesPro",
                        selectedPlan: "salesPro",
                        error: "maxNumberOfPersonas",
                      },
                      "800px"
                    );
                    break;
                }

                this.router.navigate(["/persona"]);
              } else if (duplicate && userPersonaId) {
                this.loadData(userPersonaId, true);
              }
            }
          },
          (error) => {
            this.handleNodeDeactivation(error);
          },
          () => {}
        );
      } catch (error) {}
    }
  }

  get isProLicense() {
    let planIdType = this.sharedData.getAttribute("planIdType") ?? this.sharedData.getAttribute("planId");

    return (
      !!planIdType &&
      ["sales-pro", "sales-pro-yearly", "sales-pro-monthly", "pro-plan-teams-smb", "pro-plan-teams-ent"].includes(
        planIdType
      )
    );
  }

  /**
   * Opens the "regenerate isn't in your plan" upsell (Freemium/Starter/Growth
   * — `AddFeaturesPlanSeeder` seeds `regenerate_count = 0` for those, so the
   * backend 403s every Regenerate) or, for a plan/corporate account that
   * should never see this branch, a plain toast rather than the wrong dialog.
   */
  private openRegenerateNotInPlanDialog(): void {
    if (this.quotaMeta.showUpgradeButton) {
      this.dialog.openDialogComponent(
        WarningDialogComponent,
        {
          type: "persona",
          showTheStarter: false,
          pricing: "salesPro",
          selectedPlan: "salesPro",
          error: "regenerateNotInPlan",
        },
        "800px"
      );
    } else {
      this.notification.toast(
        "Regenerating an AI Persona isn't included in your plan. Contact your account admin to upgrade.",
        "OK",
        7000
      );
    }
  }

  /**
   * Opens the "daily quota used up" dialog. Freemium/Starter/Growth get the
   * upsell; a Sales Pro or corporate (company-managed) account — nothing left
   * to upgrade to today — gets a plain info notice instead (issue #569: the
   * old code showed the "premium feature" upsell to every plan here).
   */
  private openQuotaExhaustedDialog(): void {
    const countdown = this.refillCountdown || "later today";

    if (this.quotaMeta.showUpgradeButton) {
      this.dialog.openDialogComponent(
        WarningDialogComponent,
        {
          type: "persona",
          showTheStarter: false,
          pricing: "salesPro",
          selectedPlan: "salesPro",
          error: "dailyPromptQuotaExhausted",
          messageParams: { total: this.quota.total, countdown },
        },
        "800px"
      );
    } else {
      this.dialog
        .confirm(
          {
            variant: "info",
            title: "Out of AI prompts for today",
            message: `Your quota refills in ${countdown}. You can still save your changes now and Regenerate later.`,
            confirmLabel: "OK",
          },
          { width: DIALOG_WIDTHS.sm }
        )
        .subscribe();
    }
  }

  /**
   * 403s from the generate endpoint carry `{code, error, message,
   * seconds_remaining_until_next_prompt_refill}` (node's `RawBodyError` —
   * bypasses the `{result:...}` envelope, so `error.error` here IS that
   * object directly). A stale client-side `quota.remaining` must not fail
   * silently.
   */
  private handleGenerateError(error: any): void {
    const code = error?.error?.code;
    if (code === "PERSONA_REGENERATION_NOT_IN_PLAN") {
      this.openRegenerateNotInPlanDialog();
      return;
    }
    if (code === "PROMPT_QUOTA_EXHAUSTED") {
      // The 403 body itself carries the fresh refill countdown — apply it
      // before opening the dialog so the copy is never stale even when the
      // in-memory `quota` snapshot predates this rejection (the sprint's
      // own "verify the countdown reaches the UI, not just the network
      // layer" requirement).
      const seconds = error?.error?.seconds_remaining_until_next_prompt_refill;
      if (typeof seconds === "number") {
        this.setRefillAt(seconds);
      }
      this.openQuotaExhaustedDialog();
      return;
    }

    // Every server-shaped error surfaces a message via one of these three
    // precedences — mirrors flymsg-extension's own `json.error ?? result.
    // message ?? result.data.message` chain: node bypasses the `{result:...}`
    // envelope for any body carrying a top-level `error` key (every 4xx this
    // endpoint documents, plus a 422 validation failure's `{error, error_
    // list}`), so `error.error.error`/`error.error.message` cover those; the
    // `result.message`/`result.data.message` legs are kept as a defensive
    // fallback in case a future response nests one level deeper the way some
    // other node controllers do. This restates the message ourselves since a
    // plain `HttpClient` call (no `DataService`) never runs any global toast
    // for it.
    this.notification.toast(
      error?.error?.error ||
        error?.error?.message ||
        error?.error?.result?.message ||
        error?.error?.result?.data?.message ||
        "Something went wrong generating the AI Emulation. Please try again.",
      "OK",
      7000
    );
  }

  generateAIEmulation(regenerate: boolean = false): void {
    if (regenerate) {
      if (!this.quotaMeta.regenerationAllowed) {
        this.openRegenerateNotInPlanDialog();
        return;
      }

      if (!this.canRegenerate) {
        this.openQuotaExhaustedDialog();
        return;
      }

      if (this.form.untouched) {
        this.notification.toast("To regenerate you must change something in any of the fields above.", "OK", 7000);
        return;
      }
    }

    const data = this.form.value;
    this.updating = true;
    // Whether the response itself carried a fresh quota (flymsg-be#875): if
    // so the counter is already correct and re-fetching in `complete` below
    // would just race the backend's queued usage-tracking listener again
    // (the bug this issue reports) — only re-fetch when the field is absent.
    let quotaRefreshedFromResponse = false;

    // Plain HttpClient (via PersonaAiApiService), not DataService — there is
    // no global handler to opt out of here (no `ignoreErrors`/URL-keyed 403
    // branch to suppress); this component already owns every error path for
    // this request via handleGenerateError() below.
    this.personaAiApiService
      .generatePersona({
        ...data,
        regenerate,
        // The backend charges a regenerate against an existing persona by
        // this id (flymsg-be#876 QA finding F3) — omitted on `/persona/new`
        // and on the duplicate flow, where the copy has no persisted id yet.
        ...(this.userPersonaId ? { user_persona_id: this.userPersonaId } : {}),
      })
      .subscribe(
        (response: any) => {
          if (response?.result) {
            // flymsg-be#876's real shape nests the generated text under
            // `result.data` (and, alongside it, a flat `result.quota`); the
            // legacy backend still on master today returns the text directly
            // as `result` (a string). Handle both (QA finding F1).
            const isLegacyStringResult = typeof response.result === "string";
            const generatedText = isLegacyStringResult ? response.result : response.result?.data;
            // `result` carries the quota fields only under the new shape —
            // fall back to the top level in case a future backend response
            // ever puts them there instead (QA finding F2).
            const quotaSource = !isLegacyStringResult && response.result ? response.result : response;

            if (typeof generatedText === "string" && generatedText.length > 0) {
              this.form.controls.ai_emulation.setValue(generatedText);
            }

            this.form.markAsUntouched();
            this.staleEmulation = false;

            if (quotaSource.quota) {
              // `result.quota` is the FLAT quota object, not the
              // `{ quota, show_upgrade_button, ... }` envelope
              // applyQuotaPayload() expects — apply it directly instead of
              // re-pathing into that helper (which would clobber `this.quota`
              // with `undefined` and break the template).
              this.quota = quotaSource.quota;
              this.setRefillAt(quotaSource.quota.seconds_remaining_until_next_prompt_refill);
              quotaRefreshedFromResponse = true;
            } else if (quotaSource.quota_consumed === true || (quotaSource.quota_consumed === undefined && regenerate)) {
              // Optimistic decrement — today's backend (flymsg-be#875 not
              // merged yet) never returns `quota`/`quota_consumed`, so a
              // `regenerate: true` call is assumed charged; a plain first
              // generation (`regenerate: false`) is assumed free.
              this.quota = {
                ...this.quota,
                used: this.quota.used + 1,
                remaining: this.quota.total === -1 ? this.quota.remaining : Math.max(0, this.quota.remaining - 1),
              };
            }

            this.notification.toast("AI Emulation generated successfully.", "OK", 7000);

            // smooth scroll to the bottom wrapperContainer
            setTimeout(() => {
              this.footerElement.nativeElement.scrollIntoView({
                behavior: "smooth",
              });
            }, 200);
          }
        },
        (error) => {
          this.updating = false;
          this.handleGenerateError(error);
        },
        () => {
          this.updating = false;
          if (!quotaRefreshedFromResponse) {
            // Transitional, until the BE ships `quota` on this response:
            // TrackFlyMsgAIUsageEventListener is queued, so an immediate
            // re-fetch here would race it and read back the stale count
            // (exactly the bug this issue reports) — 4s comfortably clears
            // a queued job on this stack.
            timer(4000)
              .pipe(takeUntil(this.destroy$))
              .subscribe(() => this.updatePrompts());
          }
          if (this.global.onboardingActive) {
            setTimeout(() => {
              this.tourService.initialize([
                {
                  stepId: "step16-start",
                  anchorId: "step16-start",
                  content: " ",
                  title: " ",
                },
                {
                  anchorId: "step16",
                  stepId: "step16",
                  content:
                    "Click to save the generated AI persona prompt. This persona will then be available for use in FlyMSG AI's writing tools.",
                  title: "Save Persona",
                  enableBackdrop: true,
                  disablePageScrolling: true,
                  prevBtnTitle: "Skip",
                  nextBtnTitle: "Got it!",
                  placement: {
                    xPosition: "before",
                    yPosition: "above",
                  },
                  endBtnTitle: "Got it!",
                  popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end",
                },
                {
                  anchorId: "step16-end",
                  content: " ",
                  title: " ",
                },
              ]);

              // No stepShow$ subscription here: the single subscription
              // registered once in ngOnInit() already handles step16-start
              // and step16-end (see the comment there). Re-subscribing on
              // every generation used to stack a fresh, never-unsubscribed
              // listener each time, so a second generation's step16-end
              // fired savePersona() twice (issue #466).
              setTimeout(() => {
                this.tourService.startAt("step16");
              }, 1000);
            }, 50);
          }
        }
      );
  }

  get isOnboarding(): boolean {
    return this.global.onboardingActive;
  }

  markAsUntouched(): void {
    setTimeout(() => {
      this.form.markAsUntouched();
    }, 200);
  }

  goBack(): void {
    if (!this.canDeactivate()) {
      this.dialog.confirm(LEAVE_PERSONA_CONFIRM, { width: "480px" }).subscribe((leave) => {
        if (leave) {
          this.form.markAsPristine();
          this.router.navigate(["/persona"]);
        }
      });
    } else {
      this.router.navigate(["/persona"]);
    }
  }

  copyToClipboard(): void {
    if (!this.isProLicense) {
      this.dialog.openDialogComponent(
        WarningDialogComponent,
        {
          type: "persona",
          showTheStarter: false,
          pricing: "salesPro",
          selectedPlan: "salesPro",
          error: "maxNumberOfPersonas",
        },
        "800px"
      );

      return;
    }

    if (this.clipboardService.copyFromContent(this.form.controls["ai_emulation"]?.value)) {
      this.notification.toast("AI Emulation copied to clipboard!", "Ok", 5000);
    }
  }

  /**
   * Hands the walk-me back to the onboarding modal on the FlyBoard.
   *
   * The order is load-bearing, and getting it wrong is why saving a persona
   * mid-tour used to dead-end on the dashboard with no modal. DialogService
   * closes every open dialog on NavigationStart, and Angular's Router emits
   * NavigationStart *synchronously* from inside `navigate()` — so opening the
   * modal before (or immediately after) navigating queues a `closeAll()` that
   * lands on the modal we just opened. Waiting for the navigation to settle is
   * the only ordering that survives it.
   *
   * Navigating to "/flyboard" rather than "/dashboard" also matters: the latter
   * is a `redirectTo` (dashboard.routing.ts), so it costs a second
   * NavigationStart — another `closeAll()` to race with.
   */
  private returnToOnboardingModal(step = modalStepIndex("linkedin_ai_comment_generator")): void {
    this.router.navigate(["/flyboard"]).then(() => {
      this.global.components.global.startModal(step);
    });
  }

  savePersona(): void {
    // Captured before the request: a successful save clears form.untouched
    // downstream anyway, and this is the one signal that distinguishes "the
    // user regenerated, then saved" from "the user is out of AI prompts and
    // Save Without Regenerate stayed enabled for them" (see the Save button's
    // ngbPopover in the template, which keys off the same two conditions).
    const savingWithoutRegenerate = !this.form.untouched && !this.canRegenerate;
    this.form.controls.ai_emulation.enable();
    const data = this.form.value;
    this.form.controls.ai_emulation.disable();
    this.updating = true;

    if (this.userPersonaId) {
      this.dataService
        .put({
          url: `v2/user-persona/update/${this.userPersonaId}`,
          isLoader: true,
          data,
        })
        .subscribe(
          (response: any) => {
            if (response?.result) {
              this.notification.toast(
                savingWithoutRegenerate ? "Persona saved. Regenerate it when your AI prompts refill." : "Persona updated",
                "OK",
                7000
              );

              this.form.markAsPristine();
              this.router.navigate(["/persona"]);
            }
          },
          (error) => {
            this.updating = false;
          },
          () => {
            this.updating = false;
          }
        );
    } else {
      this.dataService.post({ url: "v2/user-persona", isLoader: true, data }).subscribe(
        (response: any) => {
          if (response?.result) {
            this.form.markAsPristine();
            if (this.isOnboarding) {
              this.global.onboardingActive = false;
              this.tourService.end();
              this.returnToOnboardingModal();
            } else {
              this.notification.toast("Persona added", "OK", 7000);
              this.router.navigate(["/persona"]);
            }
          }
        },
        (error) => {
          this.updating = false;
        },
        () => {
          this.updating = false;
        }
      );
    }
  }

  /**
   * The nine chip fields are <vng-chip-input> ControlValueAccessors bound
   * straight to their string[] form controls, so the library owns Enter/comma
   * commit, paste splitting, blur-commit ([addOnBlur]) and the chip rendering
   * that used to be nine copies of add/remove/onPaste/blur here.
   *
   * vng-chip-input DOES mark the control touched — but only from its own
   * onBlur(). Removing a chip via its "x" never blurs the inner input, so
   * without this the Regenerate / Save Persona gating (which keys off
   * `form.untouched`, reset by markAsUntouched() after each generate) would
   * miss chip-only edits and let a user save without regenerating.
   */
  markChipsTouched(): void {
    this.form.markAsTouched();
  }
}
